/**
 * TEL-2243 — tradução central de mensagens de erro.
 *
 * Regra única deste módulo: a `message` crua do backend **nunca** é renderizada.
 * Só vai para a tela texto que o portal escreveu, resolvido por código conhecido.
 * Erro sem código conhecido cai no genérico amigável — é o comportamento correto,
 * não uma lacuna. Isso é o que garante o critério de aceite "nenhuma tela exibe
 * mensagem bruta": não existe degrau final que mostre a mensagem original.
 *
 * A `message` continua disponível em `rawMessage`, para log e para os pontos
 * legados que ainda ramificam nela. Ela é dado de diagnóstico, não de interface.
 *
 * Este módulo é puro: não conhece axios, não conhece Vue, não faz I/O e
 * **nunca lança**. Ele roda no interceptor de todos os clientes HTTP do portal,
 * então uma exceção aqui quebraria o tratamento de erro do portal inteiro.
 */

export const SUPPORT_PHONE = '(48) 3332-8503'
export const SUPPORT_WHATSAPP_URL = 'https://wa.me/+554833328503'

/**
 * Marcador opt-in: onde ele aparece, o ErrorAlert renderiza o telefone do
 * suporte como link do WhatsApp. Texto que cite o numero sem o marcador
 * continua sendo texto puro — a decisao e de quem escreve a frase, nao de uma
 * busca automatica por string, que e o mesmo anti-padrao de ramificar por texto.
 */
export const SUPPORT_PLACEHOLDER = '{suporte}'

const GENERIC_LINE =
  `Não conseguimos processar sua solicitação no momento. ` +
  `Tente novamente em alguns minutos ou fale com o suporte: {suporte}.`

const NOT_FOUND_LINE =
  `Não encontramos este registro. Atualize a página e tente novamente. ` +
  `Se o comportamento persistir, fale com o suporte: {suporte}.`

const PAYMENT_GENERIC_LINE =
  `Houve um erro ao gerar seu pagamento. ` +
  `Por favor, entre em contato com nosso Suporte: {suporte}.`

const FINANCIAL_INTEGRATION_LINE =
  `A integração de pagamento não está configurada para esta organização. ` +
  `Fale com o suporte: {suporte}.`

/**
 * Mapa código → mensagem. `lines` é sempre array: validação multi-campo devolve
 * várias, e o componente renderiza uma por parágrafo. `support: true` faz o
 * componente acrescentar o link do WhatsApp como elemento — nunca como HTML
 * dentro da string, para o texto seguir escapado.
 *
 * `{chave}` é preenchido no componente, com dado que a própria tela já tem.
 */
export const MESSAGES = {
  // --- Não encontrado: 16 códigos, um texto. O cliente não conserta um id que
  // sumiu; a única ação útil é recarregar. O suporte distingue qual foi pelo
  // código, na aba Network.
  CHARGE_NOT_FOUND: { lines: [NOT_FOUND_LINE] },
  CHARGE_PAYMENT_NOT_FOUND: { lines: [NOT_FOUND_LINE] },
  CHARGE_INVOICE_NOT_FOUND: { lines: [NOT_FOUND_LINE] },
  FINANCIAL_DATA_NOT_FOUND: { lines: [NOT_FOUND_LINE] },
  FINANCIAL_ACCOUNT_NOT_FOUND: { lines: [NOT_FOUND_LINE] },
  ORGANIZATION_NOT_FOUND: { lines: [NOT_FOUND_LINE] },
  ORGANIZATION_ADMIN_NOT_FOUND: { lines: [NOT_FOUND_LINE] },
  EXTENSION_NOT_FOUND: { lines: [NOT_FOUND_LINE] },
  CALL_NOT_FOUND: { lines: [NOT_FOUND_LINE] },
  ACCESS_TOKEN_NOT_FOUND: { lines: [NOT_FOUND_LINE] },
  AUTO_RECHARGE_NOT_FOUND: { lines: [NOT_FOUND_LINE] },
  CREDIT_CARD_TOKEN_NOT_FOUND: { lines: [NOT_FOUND_LINE] },
  INTEGRATION_NOT_FOUND: { lines: [NOT_FOUND_LINE] },
  USER_NOT_FOUND: { lines: [NOT_FOUND_LINE] },
  SUBSCRIPTION_NOT_FOUND: { lines: [NOT_FOUND_LINE] },
  INVOICE_NOT_FOUND: { lines: [NOT_FOUND_LINE] },

  // --- Validação em que o cliente tem o que corrigir na tela
  EXTENSION_ALREADY_EXISTS: {
    lines: ['O ramal {ramal} já existe na sua organização. Escolha outro número.']
  },
  EXTENSION_FIELDS_REQUIRED: {
    lines: ['Informe o número do ramal e a senha.']
  },
  USER_EMAIL_ALREADY_EXISTS: {
    lines: ['Este e-mail já está cadastrado. Informe outro ou recupere a senha.']
  },
  INTEGRATION_ALREADY_EXISTS: {
    lines: ['Esta integração já está configurada. Edite a existente em vez de criar outra.']
  },
  REFERRAL_CODE_ALREADY_EXISTS: {
    lines: ['Este código já está em uso. Escolha outro.']
  },
  // Textos que o backend já devolvia em pt-BR e que se perderiam no genérico.
  // Mantidos palavra por palavra — explicam o que corrigir melhor do que
  // qualquer reescrita nossa.
  EXTENSION_ASSIGNED_TO_ANOTHER_USER: {
    lines: ['O ramal {ramal} já está vinculado a outro usuário']
  },
  // Resolvido pelo `name` do erro: vem de `Users.validateAsync('phone', …)` no
  // pbxapi, que não tem onde receber `errorCode`.
  //
  // Só alcança o erro que chega SEM `details.messages` — o `resolveFieldErrors`
  // roda antes dos candidatos por código e resolve os que vêm com campo. Por
  // isso a BINA não cai aqui, mesmo o telefone dela sendo validado pelo mesmo
  // helper: ela chega como `details.messages.bina` e sai pelo FIELD_PASSTHROUGH.
  InvalidPhoneNumber: {
    lines: [
      'O número informado não é válido, por favor verifique se possui ' +
      'DDD (2 dígitos) + Número (8 ou 9 dígitos) e tente novamente'
    ]
  },

  // Desconto por seat não é editável pelo cliente no portal: orientá-lo a revisar
  // desconto seria instrução impossível de seguir. Por isso vai para o suporte.
  SUBSCRIPTION_VALUE_BELOW_MINIMUM: { lines: [PAYMENT_GENERIC_LINE] },
  SUBSCRIPTION_SEAT_DISCOUNT_EXCEEDS_VALUE: { lines: [PAYMENT_GENERIC_LINE] },

  // --- Regra de negócio
  AUTO_RECHARGE_ALREADY_ACTIVE: {
    lines: ['Já existe uma recarga automática ativa ou pausada. Edite a existente em vez de criar outra.']
  },
  AUTO_RECHARGE_SAME_VALUES: {
    lines: ['Nenhuma alteração foi feita: os valores enviados são iguais aos atuais.']
  },
  AUTO_RECHARGE_NOT_PAUSED: {
    lines: ['Esta recarga automática não está pausada.']
  },
  AUTO_RECHARGE_INACTIVE: {
    lines: ['Esta recarga automática está inativa.']
  },
  CREDIT_CARD_BRAND_NOT_SUPPORTED: {
    lines: ['A bandeira deste cartão não é aceita pela nossa processadora de pagamentos. Use um cartão de outra bandeira.']
  },
  AUTO_RECHARGE_CANNOT_CANCEL: {
    lines: [
      `Esta recarga automática não pode ser cancelada. ` +
      `Tente novamente em alguns minutos ou fale com o suporte: {suporte}.`
    ]
  },
  CHARGE_NOT_ALLOWED_FOR_UNLIMITED: {
    lines: [`Contas do tipo Plano não geram Recargas avulsas. Fale com o suporte: {suporte}.`]
  },
  SUBSCRIPTION_STATUS_NOT_EDITABLE: {
    lines: [
      `Não é possível editar esta assinatura no status atual. Fale com o suporte: {suporte}.`
    ]
  },

  // --- Permissão e sessão
  UNAUTHORIZED: {
    lines: ['Sessão expirada. Atualize a página e faça login novamente.']
  },
  // Bloqueio do limitador de requisições (429). O texto do backend é bom e já vem
  // em pt-BR, mas cita o tipo de recurso ("exportações de relatório", "reset de
  // senha") que o portal não recebe separado — por isso a frase aqui é genérica
  // quanto ao recurso e específica quanto ao que fazer.
  //
  // O que importa é NÃO mandar falar com o suporte: o bloqueio expira sozinho em
  // até um minuto, e o genérico levava o cliente a abrir chamado por algo que se
  // resolve esperando. O caso mais fácil de atingir é o reset de senha, com
  // limite de 3 por minuto.
  RATE_LIMITED: {
    lines: [
      'Você excedeu o limite de requisições por minuto. ' +
      'Tente novamente daqui alguns segundos.'
    ]
  },
  // Bloqueio do cadastro (429 na rota pública `POST /accounts/signup`), com janela
  // de 24 HORAS — por isso não reaproveita o RATE_LIMITED acima, que fala em
  // segundos. Mandar esperar segundos num bloqueio de um dia seria a mesma classe
  // de defeito que este ticket existe para corrigir.
  //
  // O irmão `PARTNER_SIGNUP_RATE_LIMITED` sai da mesma rota, mas só pelo canal de
  // parceiro (`x-partner-id` + api-key de serviço). O portal não o mapeia porque
  // não chama esse canal — entrada aqui seria código morto.
  SIGNUP_RATE_LIMITED: {
    lines: [
      'Você excedeu o limite de criação de conta nas últimas 24 horas. ' +
      'Tente novamente mais tarde.'
    ]
  },
  FORBIDDEN_ADMIN_ONLY: {
    lines: [
      'Apenas administradores podem fazer esta alteração. ' +
      'Fale com um administrador da sua organização.'
    ]
  },
  // Conta Recarga que bateu no teto de usuários ativos. O caminho normal nem chega
  // aqui: `openAddUserModal` consulta a elegibilidade antes de abrir o formulário e
  // mostra o `UserLimitReachedModal`, que traz o número e o botão de migrar. Este
  // texto cobre a corrida — outro admin ocupa a última vaga enquanto este preenche.
  //
  // Sem o número de propósito: o portal não tem o limite neste ponto, e inventá-lo
  // arriscaria divergir do backend. A frase é a mesma do fallback do
  // `UserLimitReachedModal.vue`, para os dois caminhos dizerem o mesmo.
  //
  // Resolve pelo `code` do erro (o pbxapi grava o motivo ali, não em `errorCode`).
  RECHARGE_ACTIVE_USERS_LIMIT_REACHED: {
    lines: [
      'Sua conta no modelo Recarga atingiu o limite de usuários ativos. ' +
      'Para adicionar mais usuários, migre para um plano.'
    ]
  },
  // `isUnlimited` é nome de flag interna e não pode chegar ao cliente. As quatro
  // mensagens do backend (ativação, convite, troca, preview) colapsam aqui:
  // a ação do cliente é a mesma nas quatro.
  PLAN_REQUIRED_FOR_OPERATION: {
    lines: ['Esta operação está disponível apenas para contas com plano contratado.']
  },
  ACCOUNT_RESTRICTED: {
    lines: ['Esta ação não pode ser realizada neste momento. Verifique sua assinatura ou entre em contato com nosso suporte para mais informações.']
  },

  // --- Integração
  FINANCIAL_INTEGRATION_NOT_FOUND: { lines: [FINANCIAL_INTEGRATION_LINE] },
  SIPPULSE_INTEGRATION_NOT_FOUND: {
    lines: [`A integração de telefonia não está configurada. Fale com o suporte: {suporte}.`]
  },
  // --- Faturamento (billing-service). Antes caiam todos no generico.
  INVOICE_ALREADY_PAID: {
    lines: ['Esta fatura já foi paga. Atualize a página para ver o estado atual.']
  },
  INVOICE_ALREADY_CANCELLED: {
    lines: ['Esta fatura já foi cancelada. Atualize a página para ver o estado atual.']
  },
  INVOICE_ALREADY_RELEASED: {
    lines: ['Esta fatura já foi liberada. Atualize a página para ver o estado atual.']
  },
  INVOICE_INVALID_BILLING_DAY: {
    lines: ['Dia de cobrança inválido. Escolha um dia entre 1 e 28.']
  },
  SUBSCRIPTION_NOT_ACTIVE: {
    lines: [`Não encontramos uma assinatura ativa para esta organização. Fale com o suporte: {suporte}.`]
  },
  SUBSCRIPTION_ITEM_WITHOUT_SEATS: {
    lines: ['Selecione ao menos um usuário para este plano.']
  },
  SUBSCRIPTION_NO_CURRENT_CYCLE: {
    lines: [`Não há ciclo de cobrança aberto no momento. Tente novamente em alguns minutos ou fale com o suporte: {suporte}.`]
  },
  SUBSCRIPTION_PRICE_UNRESOLVED: {
    lines: [`Não foi possível calcular o valor da assinatura. Fale com o suporte: {suporte}.`]
  },
  SUBSCRIPTION_SEAT_ALREADY_EXISTS: {
    lines: ['Este usuário já está incluído na assinatura.']
  },
  SUBSCRIPTION_SEAT_NOT_FOUND: {
    lines: ['Este usuário não está incluído na assinatura.']
  },
  SUBSCRIPTION_SEAT_SAME_PLAN: {
    lines: ['Este usuário já está neste plano.']
  },
  SEATS_DECOUPLED_REQUIRED: {
    lines: [`Recurso disponível após a migração da sua conta para licenças. Fale com o suporte: {suporte}.`]
  },
  INVITE_ALREADY_PAID: {
    lines: ['O convite já foi pago; a licença dele está sendo criada. Aguarde alguns minutos.']
  },
  SEAT_QUANTITY_INVALID: {
    lines: ['Informe uma quantidade inteira maior que zero.']
  },
  SEAT_BILLING_TYPE_REQUIRED: {
    lines: ['Escolha a forma de pagamento: cartão de crédito, Pix ou boleto.']
  },
  INVOICE_BILLING_TYPE_REQUIRED: {
    lines: ['Escolha a forma de pagamento: cartão de crédito, Pix ou boleto.']
  },
  SUBSCRIPTION_SEAT_PLAN_CHANGE_CYCLE_LIMIT: {
    lines: ['Este usuário já trocou de plano neste ciclo. A próxima troca fica disponível no próximo ciclo.']
  },
  SUBSCRIPTION_SEATS_DECOUPLED_REQUIRED: {
    lines: [`Recurso disponível após a migração da sua conta para licenças. Fale com o suporte: {suporte}.`]
  },
  SEAT_PLAN_NAME_REQUIRED: {
    lines: ['Selecione o plano.']
  },
  SEAT_ID_REQUIRED: {
    lines: ['Selecione a licença.']
  },
  SUBSCRIPTION_SEAT_ALREADY_OCCUPIED: {
    lines: ['Essa licença acabou de ser ocupada. Escolha outra.']
  },
  SUBSCRIPTION_USER_ALREADY_SEATED: {
    lines: ['Esse usuário já ocupa uma licença.']
  },
  SUBSCRIPTION_USER_CHECK_UNAVAILABLE: {
    lines: ['Não foi possível confirmar agora. Tente de novo.']
  },
  SUBSCRIPTION_NOT_ENOUGH_VACANT_SEATS: {
    lines: ['Não há licenças livres suficientes para reduzir.']
  },
  SUBSCRIPTION_SEAT_OCCUPIED: {
    lines: ['Essa licença está ocupada.']
  },
  SUBSCRIPTION_SEAT_REMOVAL_SCHEDULED: {
    lines: ['Essa licença está marcada para sair.']
  },
  SUBSCRIPTION_SEAT_PLAN_CHANGE_PENDING: {
    lines: ['Essa licença tem uma troca de plano agendada.']
  },
  INVOICE_INVALID_SEAT_QUANTITY: {
    lines: ['Quantidade entre 1 e 500.']
  },
  INVOICE_SEAT_PURCHASE_FREE_PLAN: {
    lines: ['Plano gratuito não se compra: convide direto.']
  },
  INVOICE_SEAT_PURCHASE_ALREADY_PENDING: {
    lines: ['Já existe uma compra de licenças aguardando pagamento. Pague ou cancele a compra na aba Licenças antes.']
  },
  INVOICE_SEAT_PURCHASE_IN_PROGRESS: {
    lines: ['A compra está sendo criada. Aguarde.']
  },
  INVOICE_SEAT_PURCHASE_NOT_PENDING: {
    lines: ['Esta compra não está mais aguardando pagamento: foi paga ou cancelada.']
  },
  INVOICE_SEAT_PURCHASE_NOT_FOUND: {
    lines: ['Compra de licenças não encontrada.']
  },
  SUBSCRIPTION_GATEWAY_INACTIVE: {
    lines: [
      `O meio de pagamento está indisponível no momento. Tente novamente em alguns minutos. ` +
      `Se o comportamento persistir, fale com o suporte: {suporte}.`
    ]
  },
  // Contratação pelo pbxapi (proxy do billing).
  ASAAS_INTEGRATION_REQUIRED: {
    lines: ['Integração ASAAS não configurada para esta organização.'],
    support: true
  },
  CONTRACT_PRICES_CHANGED: {
    lines: ['Os preços mudaram. Recarregue a página para ver os valores atualizados.']
  },
  CONTRACT_PLAN_NOT_OFFERED: {
    lines: ['Este plano não está disponível para contratação. Recarregue a página e escolha outro plano.']
  },
  BILLING_UNAVAILABLE: {
    lines: [
      'Não conseguimos confirmar a cobrança agora. Aguarde alguns minutos e confira seu e-mail antes de tentar de novo. ' +
      'Se não receber, fale com o suporte: {suporte}.'
    ]
  },
  CONTRACT_LICENSES_LIMIT: {
    lines: ['É possível contratar no máximo 99 licenças sem usuário por plano.']
  },
  CONTRACT_SEAT_USER_NOT_IN_ORGANIZATION: {
    lines: ['Há licença para um usuário que não pertence a esta organização. Recarregue a página e tente de novo.']
  },
  // Tabela de campanha (Black November): o billing recusa quem não tem direito a ela.
  PRICING_TABLE_NOT_ELIGIBLE: {
    lines: ['Esta condição promocional é exclusiva para contas cadastradas durante a campanha.']
  },
  PRICING_ELIGIBILITY_UNAVAILABLE: {
    lines: ['Não conseguimos confirmar a condição de preço agora. Tente novamente em alguns minutos.']
  },
  PRICING_TABLE_NOT_AVAILABLE: {
    lines: ['A promoção foi encerrada. Recarregue a página para ver os preços atuais.']
  },

  // --- Textos que já existiam em pt-BR e foram mantidos palavra por palavra.
  // Eles PRECISAM estar aqui: sem entrada no mapa, o allowlist os derrubaria
  // para o genérico e a gente perderia mensagem boa que já existe hoje.
  FINANCIAL_CPF_CNPJ_INVALID: {
    lines: ['CPF inválido ou sem endereço válido cadastrado.'],
    support: true
  },
  FINANCIAL_CPF_CNPJ_IN_USE: {
    lines: ['CPF/CNPJ já está em uso.'],
    support: true
  },
  SIGNUP_EMAIL_INVALID: { lines: ['Email inválido'] },
  SIGNUP_COMPANY_NAME_INVALID: {
    lines: ['Nome da empresa não permite gerar um domínio válido']
  },
  SIGNUP_DOMAIN_UNAVAILABLE: {
    lines: ['Não foi possível gerar um domínio único para a organização']
  },
  USER_EMAIL_REQUIRED: { lines: ['email é obrigatório'] },
  USER_PLAN_NAME_REQUIRED: { lines: ['Selecione o plano antes de continuar.'] },
  CHARGE_CANNOT_BE_CANCELED: {
    // Sem placeholder: so as telas Sippulse passam `context`, e nas demais o `{id}`
    // era removido, deixando "A recarga ID nao pode ser cancelada". O id nao ajuda
    // o cliente — o suporte tem o codigo na aba Network.
    lines: ['Esta recarga não pode ser cancelada.']
  },
  // Cobre a cobranca que existe mas ja saiu de pending/overdue. "Atualize a
  // pagina" nao resolveria — por isso nao compartilha o texto de nao encontrado.
  CHARGE_NOT_CANCELABLE: {
    lines: ['Esta cobrança não pode mais ser cancelada porque já foi paga ou processada.']
  },
  USER_PLAN_DOWNGRADE_NOT_SELF_SERVICE: {
    lines: ['Downgrade de plano não é feito pelo portal — fale com o time comercial.']
  },
  LOGIN_USER_NOT_ACTIVE: {
    lines: ['Login não permitido: usuário não está ativo']
  },

  // --- Genéricos
  TEMPORARY_FAILURE: { lines: [GENERIC_LINE] },
  UNKNOWN: { lines: [GENERIC_LINE] }
}

/**
 * Chaves do billing-service, que usa dois estilos: pontuado e UPPER_SNAKE.
 * Não unificamos lá — as chaves existentes funcionam e reescrevê-las seria risco
 * sem retorno. A normalização acontece aqui.
 */
const BILLING_KEYS = {
  'subscription.value-below-minimum': 'SUBSCRIPTION_VALUE_BELOW_MINIMUM',
  'subscription.seat-discount-exceeds-value': 'SUBSCRIPTION_SEAT_DISCOUNT_EXCEEDS_VALUE',
  'subscription.gateway-inactive': 'SUBSCRIPTION_GATEWAY_INACTIVE',
  'subscription.not-found': 'SUBSCRIPTION_NOT_FOUND',
  'invoice.not-found': 'INVOICE_NOT_FOUND',
  'internal-server-error': 'TEMPORARY_FAILURE',
  'invoice.already-cancelled': 'INVOICE_ALREADY_CANCELLED',
  'invoice.already-paid': 'INVOICE_ALREADY_PAID',
  'invoice.already-released': 'INVOICE_ALREADY_RELEASED',
  'invoice.invalid-billing-day': 'INVOICE_INVALID_BILLING_DAY',
  'invoice.no-active-subscription': 'SUBSCRIPTION_NOT_ACTIVE',
  'subscription.no-active-subscription': 'SUBSCRIPTION_NOT_ACTIVE',
  'subscription.item-without-seats': 'SUBSCRIPTION_ITEM_WITHOUT_SEATS',
  'subscription.no-current-cycle': 'SUBSCRIPTION_NO_CURRENT_CYCLE',
  'subscription.price-table-unresolved': 'SUBSCRIPTION_PRICE_UNRESOLVED',
  'subscription.seat-already-exists': 'SUBSCRIPTION_SEAT_ALREADY_EXISTS',
  'subscription.seat-not-found': 'SUBSCRIPTION_SEAT_NOT_FOUND',
  'subscription.seat-same-plan': 'SUBSCRIPTION_SEAT_SAME_PLAN',
  'subscription.seat-already-occupied': 'SUBSCRIPTION_SEAT_ALREADY_OCCUPIED',
  'subscription.user-already-seated': 'SUBSCRIPTION_USER_ALREADY_SEATED',
  'subscription.user-check-unavailable': 'SUBSCRIPTION_USER_CHECK_UNAVAILABLE',
  'subscription.not-enough-vacant-seats': 'SUBSCRIPTION_NOT_ENOUGH_VACANT_SEATS',
  'subscription.seat-occupied': 'SUBSCRIPTION_SEAT_OCCUPIED',
  'subscription.seat-removal-scheduled': 'SUBSCRIPTION_SEAT_REMOVAL_SCHEDULED',
  'subscription.seat-plan-change-pending': 'SUBSCRIPTION_SEAT_PLAN_CHANGE_PENDING',
  'invoice.invalid-seat-quantity': 'INVOICE_INVALID_SEAT_QUANTITY',
  'invoice.seat-purchase-free-plan': 'INVOICE_SEAT_PURCHASE_FREE_PLAN',
  'invoice.seat-purchase-already-pending': 'INVOICE_SEAT_PURCHASE_ALREADY_PENDING',
  'invoice.seat-purchase-in-progress': 'INVOICE_SEAT_PURCHASE_IN_PROGRESS',
  'invoice.seat-purchase-not-pending': 'INVOICE_SEAT_PURCHASE_NOT_PENDING',
  'invoice.seat-purchase-not-found': 'INVOICE_SEAT_PURCHASE_NOT_FOUND',
  'invoice.billing-type-required': 'INVOICE_BILLING_TYPE_REQUIRED',
  'subscription.seat-plan-change-cycle-limit': 'SUBSCRIPTION_SEAT_PLAN_CHANGE_CYCLE_LIMIT',
  'subscription.seats-decoupled-required': 'SUBSCRIPTION_SEATS_DECOUPLED_REQUIRED',
  'pricing.table-not-eligible': 'PRICING_TABLE_NOT_ELIGIBLE',
  'pricing.eligibility-unavailable': 'PRICING_ELIGIBILITY_UNAVAILABLE',
  'pricing.table-not-available': 'PRICING_TABLE_NOT_AVAILABLE'
}

/**
 * Chave de transição: a mensagem em inglês do backend serve para RESOLVER o
 * código enquanto nem todo erro do pbxapi carrega `errorCode`. Ela nunca é
 * renderizada — é só chave de busca. Isso desacopla o deploy: o portal funciona
 * igual antes e depois do backend passar a mandar o código.
 *
 * DÍVIDA COM PRAZO: casar por trecho de texto é frágil por natureza — uma entrada
 * frouxa já fez erro de e-mail duplicado exibir mensagem de ramal. Este array pode
 * ser removido quando todo erro 4xx do pbxapi que o portal exibe carregar
 * `errorCode`. Para conferir a cobertura:
 *   grep -c "errorCode = " ../pbxapi/common/models/*.js
 */
const LEGACY_MESSAGES = [
  ['payment not found', 'CHARGE_PAYMENT_NOT_FOUND'],
  ['invoice not found', 'CHARGE_INVOICE_NOT_FOUND'],
  ['not found or not pending/overdue', 'CHARGE_NOT_CANCELABLE'],
  ['charge not found', 'CHARGE_NOT_FOUND'],
  ['financial data not found', 'FINANCIAL_DATA_NOT_FOUND'],
  ['financial account not found', 'FINANCIAL_ACCOUNT_NOT_FOUND'],
  ['financial integration not activated', 'FINANCIAL_INTEGRATION_NOT_FOUND'],
  ['financial integration not found', 'FINANCIAL_INTEGRATION_NOT_FOUND'],
  ['credit card token not found', 'CREDIT_CARD_TOKEN_NOT_FOUND'],
  ['call not found', 'CALL_NOT_FOUND'],
  ['organization admin user not found', 'ORGANIZATION_ADMIN_NOT_FOUND'],
  ['rganization not found', 'ORGANIZATION_NOT_FOUND'],
  ['not found in your organization', 'USER_NOT_FOUND'],
  ['already exists an active or paused', 'AUTO_RECHARGE_ALREADY_ACTIVE'],
  ['has the same values', 'AUTO_RECHARGE_SAME_VALUES'],
  ['is not PAUSED', 'AUTO_RECHARGE_NOT_PAUSED'],
  ['is unactive', 'AUTO_RECHARGE_INACTIVE'],
  ['automatic recharge id', 'AUTO_RECHARGE_CANNOT_CANCEL'],
  // Ancoradas por regex: como trecho solto, 'automaticRecharge id' pegava tambem
  // "is not PAUSED", "is unactive" e "has the same values", e 'cannot be canceled'
  // pegava o cancelamento da recarga. Resolviam certo so pela ORDEM do array —
  // reordenar quebrava. Com a ancora, a ordem deixa de importar.
  [/^automaticRecharge id \S+ not found/, 'AUTO_RECHARGE_NOT_FOUND'],
  [/^charge id \S+ cannot be canceled/, 'CHARGE_CANNOT_BE_CANCELED'],
  ['Unlimited users cannot create charges', 'CHARGE_NOT_ALLOWED_FOR_UNLIMITED'],
  ['Email already exists', 'USER_EMAIL_ALREADY_EXISTS'],
  ['Extension ID', 'EXTENSION_NOT_FOUND'],
  // TEL-2243: a entrada generica 'already exists' foi removida — ela capturava
  // integracao duplicada e codigo de indicacao, dizendo ao cliente para 'escolher
  // outro ramal' numa tela sem ramal. Os quatro erros ganharam errorCode no pbxapi,
  // e o ramal duplicado resolve por EXTENSION_ALREADY_EXISTS.
  [/^Extension \S+ already exists/, 'EXTENSION_ALREADY_EXISTS'],
  ['missing required fields: ramal', 'EXTENSION_FIELDS_REQUIRED'],
  ['Integração com SipPulse não encontrada', 'SIPPULSE_INTEGRATION_NOT_FOUND'],
  ['Only administrators can', 'FORBIDDEN_ADMIN_ONLY'],
  ['isUnlimited', 'PLAN_REQUIRED_FOR_OPERATION'],
  ['Login não permitido', 'LOGIN_USER_NOT_ACTIVE'],
  ['Downgrade de plano', 'USER_PLAN_DOWNGRADE_NOT_SELF_SERVICE'],
  ['cpfCnpj already in use', 'FINANCIAL_CPF_CNPJ_IN_USE'],
  ['invalid cpf', 'FINANCIAL_CPF_CNPJ_INVALID'],
  ['Email inválido', 'SIGNUP_EMAIL_INVALID'],
  ['Nome da empresa', 'SIGNUP_COMPANY_NAME_INVALID'],
  ['domínio único', 'SIGNUP_DOMAIN_UNAVAILABLE'],
  ['email é obrigatório', 'USER_EMAIL_REQUIRED'],
  ['Unauthorized', 'UNAUTHORIZED', true]
]

/**
 * Validação de campo do LoopBack (`details.messages`). Estes erros não têm
 * `errorCode` — são gerados pelos validadores do modelo, que não têm onde
 * receber um. Resolvemos por campo + trecho da mensagem, e devolvemos UMA LINHA
 * POR CAMPO: um cadastro com três campos inválidos mostra os três, como hoje.
 */
const FIELD_MESSAGES = [
  ['email', 'already exists', 'Este e-mail já está cadastrado. Informe outro ou recupere a senha.'],
  ['email', null, 'Informe um email válido.'],
  ['additionalEmails', 'maximum 5 emails', 'Máximo de 5 emails adicionais.'],
  ['additionalEmails', null,
    'Emails adicionais inválidos. Exemplo: "contato@dominio.com,financeiro@dominio.com"'],
  ['phone', null, 'Telefone Fixo inválido. Exemplo: (22) 3333-4444'],
  ['mobilePhone', null, 'Telefone Celular inválido. Exemplo: (22) 99999-9999'],
  ['name', null, 'Informe o nome.'],
  ['cpfCnpj', null, 'CPF/CNPJ inválido. Confira os números digitados.'],
  ['company', null, 'Informe a razão social.'],
  ['addressZipCode', null, 'CEP inválido. Informe 8 dígitos, sem pontuação.']
]

/**
 * Textos que o backend JÁ devolve em pt-BR, e que são melhores do que qualquer
 * reescrita nossa. Passam verbatim: o TEL-2243 só mexe no que está em inglês.
 *
 * É por trecho porque o mesmo campo tem idioma diferente conforme o modelo — o
 * `phone` do `users` responde em português, e o do `organization-financial`, em
 * inglês. O nome do campo sozinho não distingue.
 */
const FIELD_PASSTHROUGH = [
  'O número informado não é válido',
  'Domínio já cadastrado',
  'Domínio é permitido somente caracteres alfanuméricos',
  // `extension.js` valida o formato do ramal e ja responde em pt-BR, com a faixa
  // exata. Passa adiante em vez de repetir a faixa aqui: se o regex do backend
  // mudar, o texto do portal nao fica mentindo em silencio.
  'é permitido apenas do 1000 até 99999',
  // Mesma razao para a BINA: o `extension.js` ja manda a regra inteira (DDD +
  // 8 ou 9 digitos), e a tela de Ramais nao valida o campo no navegador nem
  // escreve o formato em lugar nenhum — sem este passthrough o cliente nao tem
  // como descobrir o que esta errado.
  'A BINA informada não é válida'
]

/**
 * Rótulo de cada campo, para quando não há texto específico. Sem isso, um campo
 * fora do FIELD_MESSAGES sumia da tela e o cliente corrigia só metade do
 * formulário — ou lia o nome interno em inglês.
 */
const FIELD_LABELS = {
  accountId: 'ID da conta',
  bina: 'BINA',
  address: 'Endereço',
  addressComplement: 'Complemento',
  addressNumber: 'Número',
  addressProvince: 'Bairro',
  addressZipCode: 'CEP',
  company: 'Razão social',
  cpfCnpj: 'CPF/CNPJ',
  domain: 'Domínio',
  email: 'Email',
  mobilePhone: 'Celular',
  name: 'Nome',
  phone: 'Telefone',
  ramal: 'Ramal',
  // Campos que as telas de integração enviam dentro de `metadata`. Hoje o pbxapi
  // não os valida com nome, então estes rótulos são preventivos: se ele passar a
  // validar, o cliente lê "Subdomínio: revise este campo" em vez do genérico.
  apiKey: 'Chave de API',
  password: 'Senha',
  subdomain: 'Subdomínio',
  token: 'Token de acesso',
  url: 'URL',
  user: 'Usuário'
}

// Onde os CRMs põem o texto do erro. Espelha o ERROR_DETAIL_KEYS do pbxapi
// (lib/helpers/utils.js): é o mapeamento que o time levantou na prática.
// Ordem importa — o campo descritivo vem antes do genérico, senão um corpo OAuth
// `{ error: 'invalid_grant', error_description: '...' }` perderia o motivo.
const CRM_MESSAGE_KEYS = [
  'faultstring', 'error_description', 'error_info', 'errorMessage',
  'detail', 'description', 'message', 'error', 'err'
]

/**
 * Strings que o axios monta no proprio navegador quando o CRM nao respondeu
 * nada — rede fora, DNS, timeout, 502 de um gateway na frente dele.
 *
 * Elas nao vieram do CRM, entao anexa-las a "Resposta do CRM: …" atribui ao
 * terceiro uma frase que ele nunca mandou — e sao exatamente as mensagens cruas
 * que o TEL-2243 existe para tirar da tela. Descartadas, sobra so a primeira
 * linha, que ja explica o que aconteceu.
 *
 * Ancoradas de proposito: texto legitimo de CRM que apenas CONTENHA uma delas
 * continua passando.
 */
const TRANSPORT_NOISE = [
  /^Network Error$/,
  /^Request failed with status code \d+$/,
  /^timeout of \d+ms exceeded$/,
  /^Request aborted$/,
  // Codigos do proprio axios/node: ECONNABORTED, ERR_NETWORK, ERR_BAD_RESPONSE.
  //
  // Ancorado nos prefixos que o Node e o axios realmente usam. O padrao anterior
  // era `/^E[A-Z_]+$/`, que casava QUALQUER palavra maiuscula comecando com E —
  // e engolia codigo curto legitimo de CRM (EXPIRED_TOKEN, EMAIL_INVALID), fazendo
  // sumir a linha "Resposta do CRM: …" que este ticket preserva de proposito.
  /^(ECONN|ETIMEDOUT|ENOTFOUND|EAI_|EPIPE|EHOSTUNREACH|ENETUNREACH|EPROTO|ERR_)[A-Z_]*$/
]

function isTransportNoise (value) {
  for (let i = 0; i < TRANSPORT_NOISE.length; i++) {
    if (TRANSPORT_NOISE[i].test(value)) return true
  }
  return false
}

function isString (value) {
  return typeof value === 'string' && value.length > 0
}

function get (object, key) {
  if (!object || typeof object !== 'object') return undefined
  return object[key]
}

function result (code, extra) {
  const entry = MESSAGES[code] || MESSAGES.UNKNOWN
  return Object.assign({
    code: MESSAGES[code] ? code : 'UNKNOWN',
    lines: entry.lines.slice(),
    support: entry.support === true
  }, extra || {})
}

function resolveByMessage (message) {
  if (!isString(message)) return null
  for (let i = 0; i < LEGACY_MESSAGES.length; i++) {
    const fragment = LEGACY_MESSAGES[i][0]
    // Tres formas de casar, da mais estrita para a mais frouxa:
    //   RegExp          — ancora no formato exato da mensagem
    //   3o item `true`  — exige igualdade (trecho curto demais para substring)
    //   string          — busca por trecho
    // Casar por trecho generico ja quebrou tres vezes neste array; prefira as
    // duas primeiras ao acrescentar entrada nova.
    if (fragment instanceof RegExp) {
      if (fragment.test(message)) return LEGACY_MESSAGES[i][1]
      continue
    }
    const exactMatch = LEGACY_MESSAGES[i][2] === true
    const matches = exactMatch ? message === fragment : message.indexOf(fragment) !== -1
    if (matches) return LEGACY_MESSAGES[i][1]
  }
  return null
}

function resolveFieldErrors (details) {
  const messages = get(details, 'messages')
  if (!messages || typeof messages !== 'object') return null
  const lines = []
  let unknown = 0
  const push = line => { if (lines.indexOf(line) === -1) lines.push(line) }

  Object.keys(messages).forEach(field => {
    const raw = messages[field]
    const list = Array.isArray(raw) ? raw.map(String) : [String(raw)]
    const text = list.join(' ')

    // 1. Texto que o backend ja manda em pt-BR passa adiante, sem reescrita. O
    // filtro e POR MENSAGEM, nao por campo: o mesmo campo pode ter dois
    // validadores e so um responder em portugues — filtrando por campo, o irmao
    // em ingles pegava carona e saia emendado na mesma linha.
    const inPortuguese = list.filter(
      message => FIELD_PASSTHROUGH.some(fragment => message.indexOf(fragment) !== -1))
    if (inPortuguese.length > 0) {
      // Parte do backend responde com frase inteira ("O número informado não é
      // válido") e parte com fragmento para o LoopBack colar depois do nome do
      // campo ("é permitido apenas do 1000 até 99999"). O fragmento sozinho nao
      // diz de que campo fala, entao ganha o rotulo na frente. A inicial
      // minuscula e o que separa os dois casos — e vale para acentuada.
      inPortuguese.forEach(message => {
        const isFragment = message.charAt(0) !== message.charAt(0).toUpperCase()
        push(isFragment && FIELD_LABELS[field]
          ? FIELD_LABELS[field] + ': ' + message + '.'
          : message)
      })
      return
    }

    // 2. Traducao especifica do campo.
    for (let i = 0; i < FIELD_MESSAGES.length; i++) {
      const rule = FIELD_MESSAGES[i]
      if (rule[0] !== field) continue
      if (rule[1] !== null && text.indexOf(rule[1]) === -1) continue
      push(rule[2])
      return
    }

    // 3. Sem traducao: nomeia o campo em pt-BR em vez de sumir com ele ou de
    // expor o texto do LoopBack em ingles.
    if (FIELD_LABELS[field]) { push(FIELD_LABELS[field] + ': revise este campo.'); return }
    unknown++
  })

  // Campo que nem no dicionario esta — nome interno novo, por exemplo. Sem o
  // fechamento incondicional, um erro so de campos desconhecidos devolvia null e
  // a tela caia no generico, sem sequer dizer que o problema era do formulario.
  if (unknown > 0) {
    push('Revise os demais campos do formulário e tente novamente.')
  }
  return lines.length > 0 ? lines : null
}

/**
 * Extrai o texto que o CRM devolveu, para a exceção deliberada do TEL-2243:
 * a mensagem do CRM é preservada de propósito, para o cliente e o suporte
 * identificarem que a falha é do CRM e não nossa. Ela é renderizada como TEXTO
 * pelo componente, nunca como HTML — então preservá-la não reabre o XSS.
 */
export function describeCrmError (err) {
  const body = get(get(err, 'response'), 'data')
  const candidates = [body, err]
  for (let c = 0; c < candidates.length; c++) {
    const source = candidates[c]
    if (!source || typeof source !== 'object') continue
    const errors = get(source, 'errors')
    if (Array.isArray(errors) && errors.length > 0) {
      // Entrada sem texto legível é descartada: `String({})` produzia
      // "[object Object]" dentro de "Resposta do CRM: …", que é exatamente a
      // mensagem bruta que o ticket existe para eliminar.
      const joined = errors
        .map(entry => (isString(entry) ? entry : get(entry, 'message')))
        .filter(isString)
        .filter(entry => !isTransportNoise(entry))
        .join('; ')
      if (isString(joined)) return joined
    }
    for (let k = 0; k < CRM_MESSAGE_KEYS.length; k++) {
      const value = get(source, CRM_MESSAGE_KEYS[k])
      if (isString(value) && !isTransportNoise(value)) return value
    }
  }
  const code = get(err, 'code')
  return isString(code) && !isTransportNoise(code) ? code : ''
}

/**
 * Converte qualquer erro em mensagem amigável.
 *
 * @param {*} err  erro do axios, do LoopBack, do NestJS ou de um CRM. Qualquer
 *                 valor é aceito, inclusive `null` — a função nunca lança.
 * @param {Object} [options]
 * @param {string} [options.source]  'crm' para as integrações, que preservam o
 *                                   texto do CRM em vez de normalizá-lo.
 * @returns {{code: string, lines: string[], support: boolean,
 *            statusCode: (number|null), rawMessage: string}}
 */
export function normalizeError (err, options) {
  try {
    const response = get(err, 'response')
    const body = get(response, 'data')

    const statusCodeRaw = get(err, 'statusCode') || get(err, 'status') || get(response, 'status')
    const statusCode = typeof statusCodeRaw === 'number' ? statusCodeRaw : null

    const rawMessage = isString(get(err, 'message'))
      ? err.message
      : (isString(get(body, 'message')) ? body.message : '')

    const base = { statusCode: statusCode, rawMessage: rawMessage }

    if (get(options, 'source') === 'crm') {
      const detail = describeCrmError(err)
      const lines = ['Não foi possível conectar à integração.']
      if (isString(detail)) lines.push('Resposta do CRM: ' + detail)
      return { code: 'CRM_INTEGRATION_ERROR',
        lines: lines,
        support: false,
        statusCode: statusCode,
        rawMessage: rawMessage }
    }

    // Validação de campo do LoopBack: uma linha por campo inválido.
    const fieldLines = resolveFieldErrors(get(err, 'details') || get(body, 'details'))
    if (fieldLines) {
      return Object.assign({ code: 'FIELD_VALIDATION', lines: fieldLines, support: false }, base)
    }

    // `errorCode` é a convenção da casa no pbxapi; `key` é a do billing-service;
    // `code` vem do LoopBack e de drivers. Os três são chave, nesta ordem.
    // `name` entra por último de propósito: é o degrau mais fraco, e um código
    // de verdade sempre ganha dele. Ele resolve só por igualdade exata, então
    // nomes nativos (`Error`, `TypeError`, `ValidationError`) e os genéricos do
    // pbxapi (`NotFound`, `ServerError`, `InvalidParameter`) não são chave e
    // passam direto — de propósito, para não engolir erro de outra natureza.
    const candidates = [
      get(err, 'errorCode'), get(body, 'errorCode'),
      get(err, 'key'), get(body, 'key'),
      get(err, 'code'),
      get(err, 'name'), get(body, 'name')
    ]
    for (let i = 0; i < candidates.length; i++) {
      const raw = candidates[i]
      if (!isString(raw)) continue
      const code = BILLING_KEYS[raw] || raw
      if (MESSAGES[code]) return Object.assign(result(code), base)
    }

    const byMessage = resolveByMessage(rawMessage)
    if (byMessage) return Object.assign(result(byMessage), base)

    if (statusCode === 401) return Object.assign(result('UNAUTHORIZED'), base)
    if (statusCode !== null && statusCode >= 500) {
      return Object.assign(result('TEMPORARY_FAILURE'), base)
    }

    return Object.assign(result('UNKNOWN'), base)
  } catch (e) {
    // Blindagem: este módulo roda no interceptor de todos os clientes HTTP.
    // Qualquer falha aqui vira o genérico, nunca uma exceção que se propaga.
    return { code: 'UNKNOWN',
      lines: MESSAGES.UNKNOWN.lines.slice(),
      support: false,
      statusCode: null,
      rawMessage: '' }
  }
}

/**
 * Marca o erro de um wrapper de CRM com a mensagem ja normalizada.
 *
 * Estes wrappers usam axios direto e nao passam pelo interceptor do
 * `loopback.js`, entao a normalizacao precisa acontecer no ponto de rejeicao.
 * Fica aqui, e nao copiado em cada wrapper, para nao repetir a mesma logica em
 * cinco arquivos.
 */
export function tagCrmError (err) {
  try {
    if (err && typeof err === 'object') err.friendly = normalizeError(err, { source: 'crm' })
  } catch (e) {
    // Nunca deixa a normalizacao derrubar o tratamento de erro
  }
  return err
}

/**
 * Preenche `{chave}` com dado da própria tela — o backend não manda esses valores
 * separados, mas a tela sempre os tem, porque foi ela que os enviou.
 * Placeholder sem valor é removido junto com um espaço adjacente, para não
 * aparecer `{ramal}` na cara do cliente.
 */
export function interpolate (lines, context) {
  if (!Array.isArray(lines)) return []
  return lines.map(line => {
    if (!isString(line)) return ''
    return line.replace(/ ?\{(\w+)\}/g, (match, key) => {
      const value = get(context, key)
      if (value === undefined || value === null || value === '') return ''
      return (match.charAt(0) === ' ' ? ' ' : '') + String(value)
    })
  })
}

/**
 * Versão em texto puro do que o `ErrorAlert` renderiza: troca `{suporte}` pelo
 * telefone e resolve os demais placeholders.
 *
 * Existe para os pontos que NÃO usam o componente — modal de pagamento, wizard
 * de onboarding, banner da personificação. Sem ela, `lines.join(' ')` entrega o
 * marcador literal e o cliente lê "fale com o suporte: {suporte}." na tela.
 * Onde der para usar o `ErrorAlert`, prefira ele: lá o telefone vira link.
 */
export function friendlyPlainText (err, fallback, context) {
  try {
    const friendly = err && err.friendly
    if (!friendly || !Array.isArray(friendly.lines) || friendly.code === 'UNKNOWN') return fallback
    const resolved = friendly.lines.map(
      line => line.split(SUPPORT_PLACEHOLDER).join(SUPPORT_PHONE))
    return interpolate(resolved, context).join(' ')
  } catch (e) {
    return fallback
  }
}

export default { normalizeError, interpolate, describeCrmError, tagCrmError, friendlyPlainText, MESSAGES, SUPPORT_PHONE, SUPPORT_WHATSAPP_URL }



// WEBPACK FOOTER //
// ./src/utils/error-message.js